import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, signUp } = useAuth();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password || !confirmPassword || !displayName || !role) {
      toast({
        type: "error",
        description: "Please fill in all fields",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        type: "error",
        description: "Password must be at least 6 characters",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        type: "error",
        description: "Passwords do not match",
      });
      return;
    }

    setIsLoading(true);

    try {
      await signUp(email, password, displayName, role);
      toast({
        type: "success",
        description: "Account created successfully! Please sign in.",
      });
      // The user will be redirected to login after successful registration
    } catch (error: any) {
      console.error("Registration error:", error);

      // Clean up Firebase error messages
      let errorMessage = error.message || "Failed to create account";

      // Remove Firebase prefix and make error messages more user-friendly
      if (errorMessage.includes("Firebase:")) {
        if (errorMessage.includes("auth/email-already-in-use")) {
          errorMessage = "An account with this email already exists";
        } else if (errorMessage.includes("auth/weak-password")) {
          errorMessage = "Password is too weak";
        } else if (errorMessage.includes("auth/invalid-email")) {
          errorMessage = "Invalid email address";
        } else {
          // For other Firebase errors, remove the Firebase prefix
          errorMessage = errorMessage.replace(/^Firebase:\s*/, "");
        }
      }

      toast({
        type: "error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-dvh w-screen items-start pt-12 md:pt-0 md:items-center justify-center bg-background">
      <div className="w-full max-w-md overflow-hidden rounded-2xl gap-12 flex flex-col">
        <div className="flex flex-col items-center justify-center gap-2 px-4 text-center sm:px-16">
          <h3 className="text-xl font-semibold dark:text-zinc-50">Sign Up</h3>
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            Create an account with your email and password
          </p>
        </div>

        <form
          className="flex flex-col gap-4 px-4 sm:px-16"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-2">
            <label
              htmlFor="displayName"
              className="text-zinc-600 font-normal dark:text-zinc-400"
            >
              Full Name
            </label>
            <input
              id="displayName"
              name="displayName"
              className="text-md md:text-sm mt-1 block w-full px-3 py-2 border border-input bg-background rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              type="text"
              placeholder="John Doe"
              autoComplete="name"
              required
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="role"
              className="text-zinc-600 font-normal dark:text-zinc-400"
            >
              Account Type
            </label>
            <Select value={role} onValueChange={setRole} required>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select your account type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="community_pharmacist">Community Pharmacist</SelectItem>
                <SelectItem value="pharmacist">Pharmacist</SelectItem>
                <SelectItem value="pbm">PBM</SelectItem>
                <SelectItem value="hospital">Hospital</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className="text-zinc-600 font-normal dark:text-zinc-400"
            >
              Email Address
            </label>
            <input
              id="email"
              name="email"
              className="text-md md:text-sm mt-1 block w-full px-3 py-2 border border-input bg-background rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              type="email"
              placeholder="user@acme.com"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="password"
              className="text-zinc-600 font-normal dark:text-zinc-400"
            >
              Password
            </label>
            <input
              id="password"
              name="password"
              className="text-md md:text-sm mt-1 block w-full px-3 py-2 border border-input bg-background rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              type="password"
              placeholder="At least 6 characters"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="confirmPassword"
              className="text-zinc-600 font-normal dark:text-zinc-400"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              className="text-md md:text-sm mt-1 block w-full px-3 py-2 border border-input bg-background rounded-md shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              type="password"
              placeholder="Confirm your password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full size-4 border-b-2 border-primary-foreground mr-2"></div>
                Creating account...
              </div>
            ) : (
              "Sign Up"
            )}
          </button>

          <p className="text-center text-sm text-gray-600 mt-4 dark:text-zinc-400">
            {"Already have an account? "}
            <Link
              to="/login"
              className="font-semibold text-gray-800 hover:underline dark:text-zinc-200"
            >
              Sign in
            </Link>
            {" instead."}
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;
